import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FarmProduct } from '../components/ProductSelectionField';

interface CoordinatorFormData {
  action: 'farmer_registration' | 'farm_price';
  farmerName?: string;
  farmerPhone?: string;
  farmerRegion?: string;
  farmerKebelle?: string;
  farmerCity?: string;
  farmerArea?: string;
  farmerLandSize?: string;
  harvestStartDate?: string;
  harvestEndDate?: string;
  products?: FarmProduct[];
  productName?: string;
  origin?: string;
  price?: string;
  region?: string;
  notes?: string;
}

interface UseCoordinatorFormSubmissionProps {
  userId: string;
  formData: CoordinatorFormData;
  date: Date;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface SubmissionStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'success' | 'error';
}

export const useCoordinatorFormSubmission = ({
  userId,
  formData,
  date,
  onSuccess,
  onError
}: UseCoordinatorFormSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSteps, setSubmissionSteps] = useState<SubmissionStep[]>([]);
  const { toast } = useToast();

  const generateReferenceId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `${formData.action.toUpperCase()}-${timestamp}-${random}`.toUpperCase();
  };

  const updateStep = (stepId: string, status: SubmissionStep['status']) => {
    setSubmissionSteps(prev => 
      prev.map(step => 
        step.id === stepId ? { ...step, status } : step
      )
    );
  };

  const initializeSteps = () => {
    const steps: SubmissionStep[] = [
      { id: 'validate', label: 'Validating data', status: 'pending' },
      { id: 'upload', label: 'Uploading to server', status: 'pending' },
      { id: 'confirm', label: 'Confirming submission', status: 'pending' }
    ];
    setSubmissionSteps(steps);
  };

  const submitForm = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    initializeSteps();
    const tg = window.Telegram?.WebApp;
    const referenceId = generateReferenceId();

    try {
      // Step 1: Validation
      updateStep('validate', 'loading');
      await new Promise(resolve => setTimeout(resolve, 800)); // Visual feedback delay
      updateStep('validate', 'success');

      // Step 2: Upload
      updateStep('upload', 'loading');
      
      if (formData.action === 'farmer_registration') {
        // Submit farmer registration with harvest dates
        const { data: farmData, error: farmError } = await supabase.from('farms').insert({
          farmer_name: formData.farmerName!,
          phone_number: formData.farmerPhone!,
          region: formData.farmerRegion!,
          kebelle: formData.farmerKebelle || null,
          city: formData.farmerCity || null,
          area: formData.farmerArea || null,
          land_size: formData.farmerLandSize ? parseFloat(formData.farmerLandSize) : null,
          harvest_start_date: formData.harvestStartDate || null,
          harvest_end_date: formData.harvestEndDate || null,
          registered_by: userId,
          coordinator_id: userId,
        }).select();

        if (farmError) throw farmError;

        // Insert farm products if any
        if (formData.products && formData.products.length > 0 && farmData?.[0]) {
          const farmId = farmData[0].id;
          const farmProductsData = formData.products
            .filter(product => product.productName.trim() !== '')
            .map(product => ({
              farm_id: farmId,
              product_name: product.productName,
              expected_harvest_date: product.harvestEndDate || null,
              estimated_yield: product.estimatedYield ? parseFloat(product.estimatedYield) : null,
              status: 'planned'
            }));

          if (farmProductsData.length > 0) {
            const { error: productsError } = await supabase
              .from('farm_products')
              .insert(farmProductsData);
            
            if (productsError) throw productsError;
          }
        }
        updateStep('upload', 'success');

        // Step 3: Confirmation
        updateStep('confirm', 'loading');
        await new Promise(resolve => setTimeout(resolve, 500));
        updateStep('confirm', 'success');

        // Scroll to top and trigger success state
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Store submission in localStorage for history
        const submission = {
          id: referenceId,
          type: 'farmer_registration',
          timestamp: new Date().toISOString(),
          data: {
            farmerName: formData.farmerName,
            region: formData.farmerRegion
          },
          status: 'success'
        };
        
        const submissions = JSON.parse(localStorage.getItem('coordinator_submissions') || '[]');
        submissions.unshift(submission);
        submissions.splice(10); // Keep only last 10 submissions
        localStorage.setItem('coordinator_submissions', JSON.stringify(submissions));

        tg?.HapticFeedback?.impactOccurred('medium');
        onSuccess('Farmer registered successfully!');
      } else {
        // Submit farm price
        const { error } = await supabase.from('farm_prices').insert({
          product_name: formData.productName!,
          origin: formData.origin || null,
          price: parseFloat(formData.price!),
          farm_price_per_kg: parseFloat(formData.price!),
          region: formData.region!,
          date: date.toISOString().split('T')[0],
          product_remark: formData.notes || null,
          created_by: userId,
        });

        if (error) throw error;
        updateStep('upload', 'success');

        // Step 3: Confirmation
        updateStep('confirm', 'loading');
        await new Promise(resolve => setTimeout(resolve, 500));
        updateStep('confirm', 'success');

        // Scroll to top and trigger success state
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Store submission in localStorage for history
        const submission = {
          id: referenceId,
          type: 'farm_price',
          timestamp: new Date().toISOString(),
          data: {
            productName: formData.productName,
            price: formData.price,
            region: formData.region
          },
          status: 'success'
        };
        
        const submissions = JSON.parse(localStorage.getItem('coordinator_submissions') || '[]');
        submissions.unshift(submission);
        submissions.splice(10); // Keep only last 10 submissions
        localStorage.setItem('coordinator_submissions', JSON.stringify(submissions));

        tg?.HapticFeedback?.impactOccurred('medium');
        onSuccess('Farm price submitted successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      
      // Update failed step
      const currentStep = submissionSteps.find(step => step.status === 'loading');
      if (currentStep) {
        updateStep(currentStep.id, 'error');
      }

      const actionName = formData.action === 'farmer_registration' ? 'farmer registration' : 'farm price submission';
      
      // Enhanced error handling
      let errorMessage = `❌ Error submitting ${actionName}`;
      let errorDescription = 'Please check your data and try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('network') || error.message.includes('fetch')) {
          errorDescription = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('validation') || error.message.includes('required')) {
          errorDescription = 'Please check that all required fields are filled correctly.';
        } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
          errorDescription = 'This entry may already exist. Please check your data.';
        }
      }

      // Store failed submission for retry
      const failedSubmission = {
        id: referenceId,
        type: formData.action,
        timestamp: new Date().toISOString(),
        data: formData,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      const submissions = JSON.parse(localStorage.getItem('coordinator_submissions') || '[]');
      submissions.unshift(failedSubmission);
      submissions.splice(10);
      localStorage.setItem('coordinator_submissions', JSON.stringify(submissions));

      // Scroll to top and trigger error state
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      tg?.HapticFeedback?.impactOccurred('heavy');
      onError(`${errorMessage}. ${errorDescription}`);
    } finally {
      setIsSubmitting(false);
      // Clear steps after delay
      setTimeout(() => setSubmissionSteps([]), 3000);
    }
  };

  return { isSubmitting, submitForm, submissionSteps };
};